import { native, toast } from "../state/app";

interface Downloads {
  save(request: { filename: string; data: Blob }): Promise<unknown>;
}

/**
 * Offer a file to save. On the site it's an ordinary download; in the iOS app it goes through the
 * share sheet (Save to Files); in the claude.ai preview the frame can't download by itself, so the
 * platform asks the viewer instead.
 * Resolves false if the file wasn't offered or the viewer said no.
 */
export async function saveFile(filename: string, data: Blob): Promise<boolean> {
  if (native.shareFile) {
    try {
      return await native.shareFile(filename, data);
    } catch {
      toast("Couldn't prepare the file");
      return false;
    }
  }
  if (__PREVIEW__) {
    const downloads = (await window.claude?.use("downloads").catch(() => null)) as Downloads | null | undefined;
    if (!downloads) {
      toast("Saving files only works when this page is opened on claude.ai");
      return false;
    }
    try {
      await downloads.save({ filename, data });
      return true;
    } catch (e) {
      const code = (e as { code?: string } | undefined)?.code;
      if (code === "rate_limited") toast("Another save is waiting for an answer");
      else if (code !== "declined") toast("Couldn't save the file here");
      return false;
    }
  }
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  return true;
}
